import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { FinecoDownloadButton } from "pages/assistant/components/FinecoDownloadButton";
import { AssistantService } from "shared/services/AssistantService";
import { useToast } from "shared/components/ui/use-toast";
import styles from "pages/assistant/components/artifacts/components/fineco-portfolio/finecoPortfolio.common.module.scss";

type FinecoProductCardButtonsProps = {
  isin: string;
};

export const FinecoProductCardButtons: React.FC<FinecoProductCardButtonsProps> = ({
  isin,
}) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [generatingPdf, setGeneratingPdf] = useState<boolean>(false);

  const handleDownloadProductCard = useCallback(async () => {
    if (!isin) {
      toast({
        description: "ISIN not available",
        title: t("global.error"),
        variant: "destructive",
      });
      return;
    }

    setGeneratingPdf(true);
    try {
      const response = await AssistantService.createProductCardPdf(isin);

      const blob = new Blob([response.data], { type: "application/pdf" });
      if (blob.type === "application/pdf") {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `product-card-${isin}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        toast({
          description: t("productCard.downloadedSuccess"),
          title: t("global.success"),
        });
      } else {
        toast({
          description: t("productCard.downloadError"),
          title: t("global.error"),
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error generating product card PDF", error);
      let errorMessage = t("productCard.downloadError");

      // Better error message extraction
      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      toast({
        description: errorMessage,
        title: t("global.error"),
        variant: "destructive",
      });
    } finally {
      setGeneratingPdf(false);
    }
  }, [isin, t, toast]);

  return (
    <div className={styles.buttonContainer}>
      <FinecoDownloadButton
        click={handleDownloadProductCard}
        copy={
          !generatingPdf
            ? t("productCard.downloadProductCard")
            : t("productCard.downloadingProductCard")
        }
        disabled={generatingPdf}
      />
    </div>
  );
}; 